import { useEffect, useMemo, useState } from "react";

const DEFAULT_IMG = "https://meshumimages.blob.core.windows.net/websiteimages/defaultCar.jpg";

export default function CarImageCarousel({
  imageUrls = [],
  desktopHeightClass = "md:h-[28rem]",
}) {
  const [modalOpen, setModalOpen] = useState(false);

  const pics = useMemo(() => (imageUrls?.length ? imageUrls : [DEFAULT_IMG]), [imageUrls]);
  const isSingleImage = pics.length <= 1;
  const lead = pics[0];
  const thumbs = pics.slice(1, 5); // 4 thumbs

  useEffect(() => {
    document.body.style.overflow = modalOpen ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [modalOpen]);

  const btnReset = `
    p-0 border-0 shadow-none
    bg-transparent hover:bg-transparent active:bg-transparent
    outline-none ring-0 focus:outline-none focus:ring-0
    focus-visible:outline-none focus-visible:ring-0
    [appearance:none]
  `;

  return (
    <div className="w-full relative overflow-hidden rounded-2xl bg-slate-950">
      {/* MOBILE: single image + chip */}
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className={`md:hidden relative w-full overflow-hidden ${btnReset}`}
        style={{ WebkitTapHighlightColor: "transparent" }}
        aria-label="Open photo gallery"
      >
        <div className="w-full aspect-[4/3] bg-slate-950">
          <img src={lead || DEFAULT_IMG} alt="Primary" className="h-full w-full object-contain" />
        </div>
        {!isSingleImage && (
          <span className="absolute bottom-3 right-3 bg-white/95 text-slate-900 text-xs font-medium px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2">
            <GridIcon />
            See all photos{pics.length ? ` (${pics.length})` : ""}
          </span>
        )}
      </button>

      {/* DESKTOP: 2-column layout -> left main (3fr) | right thumbs (2fr) */}
      <div className="hidden md:block relative">
        {isSingleImage ? (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className={`overflow-hidden rounded-2xl w-full ${desktopHeightClass} ${btnReset} bg-slate-950 group`}
            style={{ WebkitTapHighlightColor: "transparent" }}
            aria-label="Open photo gallery"
          >
            <img
              src={lead || DEFAULT_IMG}
              alt="Primary"
              className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.01]"
            />
          </button>
        ) : (
          <div className="grid grid-cols-[3fr_2fr] gap-3 relative">
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className={`overflow-hidden rounded-2xl ${desktopHeightClass} ${btnReset} bg-slate-950 group`}
              style={{ WebkitTapHighlightColor: "transparent" }}
              aria-label="Open photo gallery"
            >
              <img
                src={lead || DEFAULT_IMG}
                alt="Primary"
                className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.01]"
              />
            </button>

            <div className={`grid grid-rows-2 grid-cols-2 gap-3 ${desktopHeightClass}`}>
              {Array.from({ length: 4 }).map((_, i) => {
                const src = thumbs[i] || pics[i + 1] || lead || DEFAULT_IMG;
                const isBottomRight = i === 3;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className={`overflow-hidden rounded-2xl ${btnReset} group relative`}
                    style={{ WebkitTapHighlightColor: "transparent" }}
                    aria-label={`Open photo ${i + 2}`}
                  >
                    <img
                      src={src}
                      alt=""
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                    {isBottomRight && (
                      <span className="absolute bottom-2 right-2 bg-white/95 text-slate-900 text-xs font-medium px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2">
                        <GridIcon />
                        See all photos{pics.length ? ` (${pics.length})` : ""}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* FULLSCREEN MODAL: one image per row (scroll down) */}
      {modalOpen && (
        <div
          className="fixed inset-0 bg-black/90 z-50 overflow-y-auto"
          onClick={() => setModalOpen(false)}
        >
          <button
            className="fixed top-4 right-4 text-white text-3xl z-50"
            aria-label="Close gallery"
            onClick={() => setModalOpen(false)}
          >
            &times;
          </button>

          <div
            className="mx-auto w-full max-w-5xl px-4 pt-16 pb-10 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            {pics.map((url, idx) => (
              <div
                key={idx}
                className="bg-white/5 rounded-lg flex items-center justify-center"
                style={{ minHeight: "40vh" }}
              >
                <img
                  src={url || DEFAULT_IMG}
                  alt={`Vehicle view ${idx + 1}`}
                  className="w-full h-auto object-contain max-h-[85vh] rounded-md"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* tiny grid icon for chip */
function GridIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="3" width="8" height="8" stroke="currentColor" strokeWidth="1.5" />
      <rect x="13" y="3" width="8" height="8" stroke="currentColor" strokeWidth="1.5" />
      <rect x="3" y="13" width="8" height="8" stroke="currentColor" strokeWidth="1.5" />
      <rect x="13" y="13" width="8" height="8" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
